import { useEffect, useMemo, useRef, useState } from 'react';
import { urlFile } from '../../../../AxiosConfig';
import { formatToReal } from '../../../../components/utils';
import { formatDuration, LoadFailure } from '../../booking/BookingParts';
import { ServicosService } from '../../../servicos/Service';
import { useFormHorarioClienteContext } from './Context';
import { useEstabsContext } from '../../../../contexts/EstabsContext';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';

const service = new ServicosService();

// Fica montado fora da etapa para nao repetir o preenchimento vindo da edicao ao voltar.
export const SelectServico = ({ visible = true }: { visible?: boolean }) => {
    const { estabSession } = useEstabsContext();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [servicos, setServicos] = useState([] as any);
    const hydratedFromDocRef = useRef(false);
    const {
        atendente,
        servico,
        setServico,
        servicosExtras,
        setServicosExtras,
        setHorario,
        setShowHorarios,
        setHasSelectionChanged,
        doc,
        lockServico,
        selectedSlotDuration,
        allowServicesBeyondSelectedSlot,
        setAllowServicesBeyondSelectedSlot,
    } = useFormHorarioClienteContext();

    const loadServicos = async () => {
        if (!atendente?.uid) {
            setServicos([]);
            setServico({} as any);
            setServicosExtras([]);
            return;
        }

        try {
            setLoading(true);
            setLoadError(false);
            const { data } = await service.getByEstabId(estabSession._id, atendente.uid);
            const nextServicos = Array.isArray(data) ? data : [];
            setServicos(nextServicos);

            if (!hydratedFromDocRef.current && doc.servico_id) {
                const docServico = nextServicos.find((e: any) => e._id === doc.servico_id);
                if (docServico) {
                    setServico(docServico);
                }
            }

            if (
                !hydratedFromDocRef.current &&
                Array.isArray(doc.servicos_extras_ids) &&
                doc.servicos_extras_ids.length
            ) {
                const extras = nextServicos.filter((e: any) =>
                    doc.servicos_extras_ids.includes(e._id) && e._id !== doc.servico_id,
                );
                setServicosExtras(extras);
            }

            hydratedFromDocRef.current = true;

            if (servico?._id && !nextServicos.some((item: any) => item?._id === servico._id)) {
                setServico({} as any);
                setServicosExtras([]);
                setHorario(0);
                setShowHorarios(false);
            } else if (servicosExtras.length) {
                const filteredExtras = servicosExtras.filter((extra: any) =>
                    nextServicos.some((item: any) => item?._id === extra?._id),
                );
                if (filteredExtras.length !== servicosExtras.length) {
                    setServicosExtras(filteredExtras);
                }
            }
        } catch (err) {
            console.log(err);
            setServicos([]);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!!estabSession?._id) loadServicos();
    }, [estabSession?._id, atendente?.uid]);

    useEffect(() => {
        hydratedFromDocRef.current = false;
    }, [doc?._id]);

    const selectedServicesDuration = useMemo(() => {
        return [servico, ...servicosExtras].reduce(
            (total, item) => total + Number(item?.duration || 0),
            0,
        );
    }, [servico, servicosExtras]);

    const hasSelectedSlotLimit = Number(selectedSlotDuration || 0) > 0;
    const shouldLimitBySelectedSlot = hasSelectedSlotLimit && !allowServicesBeyondSelectedSlot;

    const canAddServicoToSelectedSlot = (item: any) => {
        if (!shouldLimitBySelectedSlot) return true;
        const itemDuration = Number(item?.duration || 0);
        if (!Number.isFinite(itemDuration) || itemDuration <= 0) return false;
        return selectedServicesDuration + itemDuration <= selectedSlotDuration;
    };

    if (!visible) return null;

    if (loading) {
        return (
            <div className="space-y-2" role="status" aria-label="Carregando serviços">
                {[0, 1, 2, 3].map((item) => <div key={item} className="sf-skeleton h-20 rounded-2xl" />)}
            </div>
        );
    }

    if (loadError) {
        return <LoadFailure message="Não foi possível carregar os serviços." onRetry={loadServicos} />;
    }

    const handleToggleServico = (item: any) => {
        if (lockServico) return;
        setHasSelectionChanged(true);
        // Na faixa selecionada na agenda, o inicio ja foi escolhido: trocar o servico nao o apaga.
        setHorario(hasSelectedSlotLimit ? Number(doc?.startMinutes || 0) : 0);
        setShowHorarios(false);

        const itemId = item?._id;
        const alreadySelected =
            servico?._id === itemId ||
            servicosExtras.some((extra) => extra?._id === itemId);

        if (!alreadySelected && !canAddServicoToSelectedSlot(item)) {
            return;
        }

        if (!servico?._id) {
            setServico(item);
            return;
        }

        if (servico._id === item._id) {
            if (servicosExtras.length) {
                const [nextMain, ...rest] = servicosExtras;
                setServico(nextMain);
                setServicosExtras(rest);
            } else {
                setServico({} as any);
            }
            return;
        }

        const isExtra = servicosExtras.some((extra) => extra._id === item._id);
        if (isExtra) {
            setServicosExtras((prev: any[]) =>
                prev.filter((extra) => extra._id !== item._id),
            );
            return;
        }

        setServicosExtras((prev: any[]) => [...prev, item]);
    };

    const handleAllowServicesBeyondSlotChange = (checked: boolean) => {
        setAllowServicesBeyondSelectedSlot(checked);
        setHasSelectionChanged(true);
        setServico({} as any);
        setServicosExtras([]);
        setHorario(0);
        setShowHorarios(false);
    };

    const selectedIds = new Set<string>([
        servico?._id,
        ...servicosExtras.map((extra) => extra?._id),
    ].filter(Boolean));

    const visibleServicos = lockServico
        ? servicos.filter((item: any) => selectedIds.has(item?._id))
        : shouldLimitBySelectedSlot
          ? servicos.filter((item: any) => {
              const itemId = item?._id;
              if (selectedIds.has(itemId)) return true;
              return canAddServicoToSelectedSlot(item);
          })
          : servicos;

    const selectedSlotControl = hasSelectedSlotLimit && !lockServico ? (
        <div className="rounded-2xl bg-base-content/5 p-4">
            <label className="flex cursor-pointer flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-sm font-medium text-base-content">
                    Adicionar mais serviços na faixa selecionada
                </span>
                <input
                    type="checkbox"
                    className="toggle toggle-primary"
                    checked={allowServicesBeyondSelectedSlot}
                    onChange={(event) => handleAllowServicesBeyondSlotChange(event.target.checked)}
                />
            </label>
            <div className="mt-2 flex flex-wrap gap-2 text-xs text-base-content/60">
                <span>
                    Faixa: {formatDuration(selectedSlotDuration)}
                </span>
                <span>
                    Selecionado: {formatDuration(selectedServicesDuration)}
                </span>
            </div>
        </div>
    ) : null;

    if (!atendente?.uid) {
        return (
            <div className="text-center text-base-content/60 py-6">
                Selecione um atendente para carregar os serviços.
            </div>
        );
    }

    if (lockServico && (!servico?._id || !visibleServicos.length)) {
        return (
            <div className="text-center text-base-content/60 py-6">
                Serviço da fila não encontrado.
            </div>
        );
    }

    if (!lockServico && !servicos.length) {
        return (
            <div className="w-full">
                <div className="w-full rounded-2xl bg-base-content/5 p-5 text-center space-y-3">
                    <h3 className="text-base font-semibold text-base-content">Nenhum serviço cadastrado</h3>
                    <p className="text-sm text-base-content/70">
                        Para concluir o agendamento, adicione pelo menos um serviço.
                    </p>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => navigate('/servicos?action=quick-add')}
                    >
                        Adicionar serviço rápido
                    </button>
                </div>
            </div>
        );
    }

    if (!lockServico && !visibleServicos.length) {
        return (
            <div className="w-full">
                <div className="flex w-full flex-col gap-3">
                    {selectedSlotControl}
                    <div className="rounded-2xl bg-base-content/5 p-5 text-center space-y-2">
                        <h3 className="text-base font-semibold text-base-content">Nenhum serviço cabe neste horário</h3>
                        {hasSelectedSlotLimit && (
                            <p className="text-sm text-base-content/70">
                                A faixa selecionada tem {formatDuration(selectedSlotDuration)} disponíveis.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {selectedSlotControl}
            <div>
                {!lockServico && <p className="mb-2 px-1 text-sm text-base-content/60">Você pode escolher mais de um.</p>}
                <ul className="overflow-hidden rounded-2xl bg-base-100 ring-1 ring-base-content/[0.06]">
                    {visibleServicos.map((item: any) => {
                        const selected = selectedIds.has(item._id);
                        const image = item?.imagesIds?.[0];
                        return (
                            <li key={item._id} className="border-b border-base-content/[0.08] last:border-b-0">
                                <button
                                    type="button"
                                    aria-pressed={selected}
                                    disabled={lockServico}
                                    className={`flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors ${selected ? 'bg-primary/[0.07]' : 'active:bg-base-content/5'}`}
                                    onClick={() => handleToggleServico(item)}
                                >
                                    {!!image && <img src={`${urlFile}/${encodeURIComponent(image)}`} alt="" loading="lazy" className="h-14 w-14 shrink-0 rounded-xl bg-base-200 object-cover" />}
                                    <span className="min-w-0 flex-1">
                                        <span className="block break-words text-[0.9375rem] font-semibold leading-snug text-base-content">{item?.name}</span>
                                        {!!item?.description && <span className="mt-0.5 line-clamp-2 text-sm leading-snug text-base-content/55">{item.description}</span>}
                                        <span className="mt-1 block text-sm tabular-nums text-base-content/70">
                                            {!!item?.duration && `${formatDuration(item.duration)} · `}
                                            <span className="font-medium text-base-content">{Number(item?.price) > 0 ? `R$ ${formatToReal(item.price)}` : 'Sem valor'}</span>
                                        </span>
                                    </span>
                                    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[0.6875rem] transition-colors ${selected ? 'bg-primary text-primary-content' : 'ring-1 ring-inset ring-base-content/20'}`} aria-hidden="true">
                                        {selected && <Check aria-hidden="true" />}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
};
